import React from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";
import { C } from "../ipd-billing/constants";
import SvgIcon from "../ipd-billing/SvgIcon";
import { RULE_TYPE_STYLES } from "./engine";

export { C };

// Kept for the lab/radiology screens that still render plain inputs with it.
export const inputStyle = {
  width: "100%", boxSizing: "border-box", padding: "9px 12px",
  border: `1.5px solid ${C.border}`, borderRadius: 8,
  fontSize: 13, color: C.text, background: "#fff",
  fontFamily: "inherit", outline: "none",
};

// Makes the MUI outlined controls read like `inputStyle` so the rule screens
// keep one visual language while using real MUI components.
export const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontFamily: "inherit",
    fontSize: 13,
    color: C.text,
  },
  "& .MuiOutlinedInput-input": { padding: "9px 12px", height: "auto" },
  "& .MuiInputBase-multiline": { padding: "9px 12px" },
  "& .MuiInputBase-multiline .MuiOutlinedInput-input": { padding: 0 },
  "& .MuiSelect-select.MuiOutlinedInput-input": { padding: "9px 12px" },
  "& .MuiAutocomplete-inputRoot": { paddingTop: 0, paddingBottom: 0, paddingLeft: 0 },
  "& .MuiOutlinedInput-notchedOutline": { borderWidth: "1.5px", borderColor: C.border },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: C.border },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1.5px", borderColor: C.primary,
  },
  "& .MuiFormHelperText-root": { marginLeft: 0, fontSize: 10 },
};

const monoSx = {
  "& .MuiOutlinedInput-root": { fontFamily: "monospace" },
};

// Style layers share selector keys (`& .MuiOutlinedInput-root`), so a plain
// spread would drop the base field styling instead of adding to it.
const mergeSx = (...layers) => {
  const out = {};
  layers.forEach((layer) => {
    if (!layer) return;
    Object.entries(layer).forEach(([key, val]) => {
      const canMerge = val && typeof val === "object" && !Array.isArray(val)
        && out[key] && typeof out[key] === "object" && !Array.isArray(out[key]);
      out[key] = canMerge ? { ...out[key], ...val } : val;
    });
  });
  return out;
};

export const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700, color: C.textSub,
  letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 5,
};

export const cardStyle = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
  boxShadow: "0 2px 8px rgba(13,35,63,0.06)",
};

export function Field({ label, children, style }) {
  return (
    <div style={{ ...style }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

const controlSx = (width, mono, sx) => mergeSx(
  fieldSx,
  mono ? monoSx : null,
  width != null ? { width } : null,
  sx,
);

export function Input({ width, mono, sx, ...rest }) {
  return (
    <TextField
      size="small"
      fullWidth={width == null}
      sx={controlSx(width, mono, sx)}
      {...rest}
    />
  );
}

export function TextArea({ rows = 2, width, sx, ...rest }) {
  return (
    <TextField
      size="small"
      multiline
      rows={rows}
      fullWidth={width == null}
      sx={controlSx(width, false, sx)}
      {...rest}
    />
  );
}

// `children` are MenuItem / ListSubheader nodes.
export function Select({ width, sx, children, ...rest }) {
  return (
    <TextField
      select
      size="small"
      fullWidth={width == null}
      sx={controlSx(width, false, sx)}
      {...rest}
    >
      {children}
    </TextField>
  );
}

export function Check({ checked, onChange, label, align = "center", sx }) {
  const box = (
    <Checkbox
      checked={checked}
      onChange={onChange}
      size="small"
      sx={{ color: C.border, padding: "4px", "&.Mui-checked": { color: C.primary } }}
    />
  );
  if (label == null) return box;
  return (
    <FormControlLabel
      control={box}
      label={label}
      sx={{
        margin: 0,
        gap: "6px",
        alignItems: align === "start" ? "flex-start" : "center",
        "& .MuiFormControlLabel-label": { fontSize: 13, color: C.text, fontFamily: "inherit" },
        ...sx,
      }}
    />
  );
}

const pickerSlotProps = (width, required) => ({
  textField: {
    size: "small",
    fullWidth: width == null,
    sx: controlSx(width, false, null),
  },
  field: { clearable: !required },
});

// Date/time values stay the plain strings the rule engine already stores
// (`YYYY-MM-DD`, `YYYY-MM-DDTHH:mm`, `HH:mm`) — only the editor is MUI.
export function DateInput({ value, onChange, minDate, width, required }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        value={value ? dayjs(value) : null}
        minDate={minDate ? dayjs(minDate) : undefined}
        format="DD/MM/YYYY"
        onChange={(d) => onChange(d && d.isValid() ? d.format("YYYY-MM-DD") : "")}
        slotProps={pickerSlotProps(width, required)}
      />
    </LocalizationProvider>
  );
}

export function DateTimeInput({ value, onChange, width }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        value={value ? dayjs(value) : null}
        format="DD/MM/YYYY HH:mm"
        ampm={false}
        onChange={(d) => onChange(d && d.isValid() ? d.format("YYYY-MM-DDTHH:mm") : "")}
        slotProps={pickerSlotProps(width)}
      />
    </LocalizationProvider>
  );
}

export function TimeInput({ value, onChange, width }) {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TimePicker
        value={value ? dayjs(`2000-01-01T${value}`) : null}
        format="HH:mm"
        ampm={false}
        onChange={(d) => onChange(d && d.isValid() ? d.format("HH:mm") : "")}
        slotProps={pickerSlotProps(width)}
      />
    </LocalizationProvider>
  );
}

const BTN_VARIANTS = {
  primary: {
    variant: "contained",
    sx: {
      background: C.primary, color: "#fff",
      "&:hover": { background: C.primaryHover },
      "&.Mui-disabled": { background: C.primary, color: "#fff", opacity: 0.65 },
    },
  },
  outline: {
    variant: "outlined",
    sx: {
      color: C.text, borderColor: C.border, borderWidth: "1.5px",
      "&:hover": { borderColor: C.border, borderWidth: "1.5px", background: "#f8f9fa" },
      "&.Mui-disabled": { color: C.text, borderColor: C.border, opacity: 0.65 },
    },
  },
  ghost: {
    variant: "text",
    sx: {
      color: C.textSub,
      "&:hover": { background: "#f1f5f9" },
      "&.Mui-disabled": { color: C.textSub, opacity: 0.65 },
    },
  },
  danger: {
    variant: "text",
    sx: {
      color: C.danger,
      "&:hover": { background: "#fdecea" },
      "&.Mui-disabled": { color: C.danger, opacity: 0.65 },
    },
  },
};

export function Btn({ children, onClick, variant = "primary", size = "md", disabled, style, type = "button", sx }) {
  const v = BTN_VARIANTS[variant] ?? BTN_VARIANTS.primary;
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled}
      disableElevation
      variant={v.variant}
      style={style}
      sx={mergeSx(
        {
          textTransform: "none",
          fontFamily: "inherit",
          fontWeight: 600,
          borderRadius: "8px",
          minWidth: "auto",
          lineHeight: 1.4,
          gap: "6px",
          fontSize: size === "sm" ? 12 : 13,
          padding: size === "sm" ? "6px 12px" : "9px 16px",
        },
        v.sx,
        sx,
      )}
    >
      {children}
    </Button>
  );
}

// Selectable card (domain presets, rule templates) — a button, so it gets
// keyboard focus and ripple instead of being a styled <div>.
export function CardButton({ selected, onClick, children }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        display: "block", textAlign: "left", width: "100%", padding: "12px",
        borderRadius: "10px", fontFamily: "inherit",
        border: `1.5px solid ${selected ? C.primary : C.border}`,
        background: selected ? C.accent : "#fff",
      }}
    >
      {children}
    </ButtonBase>
  );
}

const FLASH_TONES = {
  success: { severity: "success", bg: "#d1f2db", color: "#145a32", border: "#a3d9b1" },
  error: { severity: "error", bg: "#fdecea", color: "#842029", border: "#f5c2c7" },
  warning: { severity: "warning", bg: "#fff8e1", color: "#856404", border: "#ffe69c" },
};

export function Flash({ message, tone = "success", onDismiss }) {
  if (!message) return null;
  const t = FLASH_TONES[tone] ?? FLASH_TONES.success;
  return (
    <Alert
      severity={t.severity}
      onClose={onDismiss}
      sx={{
        padding: "2px 16px", borderRadius: "8px", fontSize: 13, fontWeight: 500,
        fontFamily: "inherit",
        background: t.bg, color: t.color, border: `1px solid ${t.border}`,
        alignItems: "center",
        "& .MuiAlert-icon": { color: t.color },
      }}
    >
      {message}
    </Alert>
  );
}

export function PageHeader({ title, subtitle, back, onBack, actions }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
      <div>
        {back && (
          <Button
            onClick={onBack}
            startIcon={<SvgIcon name="back" size={14} color={C.textSub} />}
            sx={{
              textTransform: "none", fontFamily: "inherit", fontSize: 12, color: C.textSub,
              padding: 0, minWidth: "auto", marginBottom: "6px",
              "&:hover": { background: "none", color: C.text },
            }}
          >
            {back}
          </Button>
        )}
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>{title}</h2>
        {subtitle && <p style={{ margin: "4px 0 0", fontSize: 13, color: C.textSub }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{actions}</div>}
    </div>
  );
}

export function KpiTile({ icon, label, value, hint, tone = "primary" }) {
  const tones = {
    primary: { bg: "#E9F5FE", color: C.primary },
    success: { bg: "#d1f2db", color: C.success },
    info: { bg: "#e0e7ff", color: "#4338ca" },
    teal: { bg: "#d1f2db", color: "#0f766e" },
  };
  const t = tones[tone] || tones.primary;
  return (
    <div style={{ ...cardStyle, padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: t.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <SvgIcon name={icon} size={18} color={t.color} />
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{value}</div>
        {hint && <div style={{ fontSize: 10, color: C.textSub }}>{hint}</div>}
      </div>
    </div>
  );
}

export function RuleTypeChip({ type }) {
  const s = RULE_TYPE_STYLES[type] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span style={{ padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>
      {type}
    </span>
  );
}

// `width` overrides the wide/narrow presets — used by editor-style modals
// (e.g. the radiology report editor) that need more room than 720px.
export function Modal({ open, onClose, title, description, children, footer, wide, width, zIndex = 1100 }) {
  if (!open) return null;
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: C.card, borderRadius: 14, width: width || (wide ? 720 : 520), maxWidth: "94vw",
        maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 16px 56px rgba(0,0,0,0.18)",
      }}>
        <div style={{ padding: "20px 24px 14px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: C.text }}>{title}</h3>
              {description && <p style={{ margin: "4px 0 0", fontSize: 12, color: C.textSub }}>{description}</p>}
            </div>
            <IconButton onClick={onClose} size="small" aria-label="Close">
              <SvgIcon name="x" size={18} color={C.textSub} />
            </IconButton>
          </div>
        </div>
        <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1 }}>{children}</div>
        {footer && (
          <div style={{ padding: "14px 24px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ChipToggle({ active, onClick, children }) {
  return (
    <Chip
      label={children}
      onClick={onClick}
      variant={active ? "filled" : "outlined"}
      sx={{
        height: "auto", borderRadius: "20px", fontFamily: "inherit",
        fontSize: 12, fontWeight: 600, borderWidth: "1.5px",
        background: active ? C.primary : "#fff",
        color: active ? "#fff" : C.text,
        borderColor: active ? C.primary : C.border,
        "& .MuiChip-label": { padding: "6px 14px" },
        "&:hover": { background: active ? C.primaryHover : "#f8f9fa" },
      }}
    />
  );
}

export function TabBar({ tabs, value, onChange }) {
  return (
    <Tabs
      value={value}
      onChange={(e, v) => onChange(v)}
      variant="scrollable"
      scrollButtons="auto"
      sx={{
        minHeight: "unset",
        borderBottom: `1px solid ${C.border}`,
        marginBottom: "16px",
        "& .MuiTabs-indicator": { backgroundColor: C.primary, height: 2 },
      }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.id}
          value={t.id}
          label={t.label}
          icon={t.icon ? <SvgIcon name={t.icon} size={14} color={value === t.id ? C.primary : C.textSub} /> : undefined}
          iconPosition="start"
          sx={{
            textTransform: "none", fontFamily: "inherit", fontSize: 13,
            minHeight: "unset", minWidth: "auto", padding: "10px 16px", gap: "6px",
            color: C.textSub,
            "&.Mui-selected": { color: C.primary, fontWeight: 700 },
          }}
        />
      ))}
    </Tabs>
  );
}

export function LoadingBlock({ message = "Loading…" }) {
  return (
    <div style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: 13 }}>
      {message}
    </div>
  );
}

export function DataTable({ columns, rows, emptyText, onRowAction }) {
  return (
    <div style={{ ...cardStyle, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f8f9fa", fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {columns.map((col) => (
              <th key={col.key} style={{ textAlign: col.align || "left", padding: "12px 16px" }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} style={{ borderTop: `1px solid ${C.border}` }} className="rb-table-row">
              {columns.map((col) => (
                <td key={col.key} style={{ padding: "12px 16px", textAlign: col.align || "left", verticalAlign: "middle" }}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} style={{ padding: 32, textAlign: "center", color: C.textSub, fontSize: 13 }}>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <style>{`.rb-table-row:hover { background: #f8f9fa; }`}</style>
    </div>
  );
}
